import { useCallback } from "react";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSButton,
  MDSConfigurationListRow,
  MDSConfigurationListRowSlotName,
  MDSRadio,
  MDSRadioGroup,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSLink,
} from "@/mds";
import { useAppStore } from "@/store";
import { IntegrationsLayoutBottomBuffer } from "@/pages/integrations/shared/integrations-layout";
import {
  StyledContentWrapper,
  StyledHeading,
} from "@/pages/integrations/shared/integrations.styles";
import { IntegrationsCalendarHero } from "@/pages/integrations/calendar/components/calendar-hero";
import { CalendarConnectionsSection } from "@/components/calendar/calendar-connections-section";
import { resolveDefaultCalendarEventTemplateSyncModelUuid } from "@/modules/uuid/sync-models/resolveDefaultCalendarEventTemplateSyncModelUuid";

export const IntegrationsCalendarPageContent = observer(() => {
  const { store } = useAppStore();
  const isConnected = store.calendar.google.isConnected || store.calendar.outlook.isConnected;
  const isLoading = !store.calendar.google.isReady || !store.calendar.outlook.isReady;

  const goToDefaultCalendarEventTemplate = useCallback(() => {
    const spaceAccountId = store.spaceAccounts.myPersonalSpaceAccountId;
    void store.navigation.goToTemplate({
      templateId: resolveDefaultCalendarEventTemplateSyncModelUuid({
        spaceAccountId,
      }),
    });
  }, [store.navigation, store.spaceAccounts.myPersonalSpaceAccountId]);

  return (
    <StyledContentWrapper>
      <IntegrationsCalendarHero isConnected={isConnected} isLoading={isLoading} />

      <CalendarConnectionsSection />

      {isConnected && (
        <ListStack>
          <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            MEETING NOTIFICATIONS
          </StyledHeading>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Manage meeting notifications for scheduled meetings and unscheduled calls in{" "}
            <MDSLink
              onPress={() => {
                store.settingsModal.open(SettingsTabId.NotificationPreferencesPage);
              }}
            >
              Settings
            </MDSLink>
          </MDSText>
        </ListStack>
      )}

      {isConnected && (
        <ListStack>
          <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            FORMATTING
          </StyledHeading>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Mem will automatically summarize meetings, adding an informative title and a concise
            summary of key points. You can further customize your note at any time.
          </MDSText>
          <MDSRadioGroup aria-label="Formatting" value="simple" onChange={() => {}}>
            <MDSConfigurationListRow
              title="Templated"
              description="Start all meeting notes with a standard template"
            >
              <MDSRadio
                slot={MDSConfigurationListRowSlotName.LeftAddon}
                value="simple"
                aria-label="Simple formatting"
              />
              <MDSButton
                slot={MDSConfigurationListRowSlotName.RightAddon}
                onClick={goToDefaultCalendarEventTemplate}
              >
                Customize
              </MDSButton>
            </MDSConfigurationListRow>
          </MDSRadioGroup>
        </ListStack>
      )}

      <IntegrationsLayoutBottomBuffer />
    </StyledContentWrapper>
  );
});

const ListStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
  max-width: 784px;
`;
